import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondary,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Grid,
  Snackbar,
  Alert
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from './PageLayout';
import { getContrastText } from '../utils/colorUtils';

function Events({ customColors }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: null,
    time: null
  });
  const [error, setError] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [successSnackbar, setSuccessSnackbar] = useState(false);

  // Carregar eventos do usuário
  useEffect(() => {
    const loadEvents = async () => {
      if (!currentUser?.uid) return;

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const loadedEvents = (userData.events || []).map(event => ({
            ...event,
            date: event.date ? new Date(event.date) : null,
            time: event.time ? new Date(event.time) : null
          }));
          setEvents(loadedEvents);
        }
      } catch (error) {
        console.error('Erro ao carregar eventos:', error);
        setError('Erro ao carregar eventos. Tente novamente.');
      }
    };

    loadEvents();
  }, [currentUser]);

  // Função auxiliar para formatar datas com segurança
  const formatDate = (date) => {
    try {
      if (!date || !(date instanceof Date) || isNaN(date)) return '';
      return format(date, "dd 'de' MMMM',' yyyy", { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  // Função auxiliar para formatar horário com segurança
  const formatTime = (time) => {
    try {
      if (!time || !(time instanceof Date) || isNaN(time)) return '';
      return format(time, 'HH:mm');
    } catch (error) {
      console.error('Erro ao formatar horário:', error);
      return '';
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewEvent({
      title: '',
      description: '',
      date: null,
      time: null
    });
  };

  const handleAddEvent = async () => {
    if (!currentUser?.uid) {
      console.error('Usuário não autenticado');
      return;
    }

    if (!newEvent.title.trim()) {
      setError('O título é obrigatório');
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const eventData = {
        id: Date.now().toString(),
        title: newEvent.title.trim(),
        description: newEvent.description.trim(),
        date: newEvent.date ? newEvent.date.toISOString() : null,
        time: newEvent.time ? newEvent.time.toISOString() : null,
        createdAt: new Date().toISOString(),
        completed: false,
        completedAt: null
      };

      // Adicionar o evento ao array de eventos do usuário
      await updateDoc(userRef, {
        events: arrayUnion(eventData)
      });

      // Atualizar o estado local com as datas convertidas
      const newEventWithDates = {
        ...eventData,
        date: newEvent.date,
        time: newEvent.time
      };
      
      setEvents(prevEvents => [...prevEvents, newEventWithDates]);
      
      // Limpar o formulário
      setNewEvent({
        title: '',
        description: '',
        date: null,
        time: null
      });
      setError('');
      setOpenDialog(false);
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
      setError('Erro ao salvar o evento. Tente novamente.');
    }
  };

  const handleRemoveAllEvents = async () => {
    if (!currentUser?.uid || !window.confirm('Tem certeza que deseja remover todos os eventos?')) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        events: []
      });

      setEvents([]);
    } catch (error) {
      console.error('Erro ao remover eventos:', error);
    }
  };

  const handleEditEvent = async () => {
    if (!currentUser?.uid) return;

    try {
      if (!editingEvent.title.trim()) {
        setError('O título do evento é obrigatório');
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const events = userData.events || [];
      
      // Encontrar o evento no array
      const eventIndex = events.findIndex(e => e.id === editingEvent.id);
      
      if (eventIndex !== -1) {
        // Corrigir: salvar date e time como string ISO no Firebase
        const updatedEvent = {
          ...events[eventIndex],
          title: editingEvent.title,
          description: editingEvent.description,
          date: editingEvent.date instanceof Date && !isNaN(editingEvent.date)
            ? editingEvent.date.toISOString()
            : (typeof editingEvent.date === 'string' ? editingEvent.date : null),
          time: editingEvent.time instanceof Date && !isNaN(editingEvent.time)
            ? editingEvent.time.toISOString()
            : (typeof editingEvent.time === 'string' ? editingEvent.time : null),
          completed: events[eventIndex].completed,
          completedAt: events[eventIndex].completedAt
        };
        // Remover o evento antigo e adicionar o atualizado
        await updateDoc(userRef, {
          events: arrayRemove(events[eventIndex])
        });
        await updateDoc(userRef, {
          events: arrayUnion(updatedEvent)
        });
        // Atualizar o estado local (usa objeto Date para uso no frontend)
        setEvents(prev => prev.map(e => 
          e.id === editingEvent.id
            ? {
                ...updatedEvent,
                date: editingEvent.date instanceof Date ? editingEvent.date : (editingEvent.date ? new Date(editingEvent.date) : null),
                time: editingEvent.time instanceof Date ? editingEvent.time : (editingEvent.time ? new Date(editingEvent.time) : null)
              }
            : e
        ));
        setEditingEvent(null);
        setError('');
        setOpenEditDialog(false);
        setSuccessSnackbar(true);
      }
    } catch (error) {
      console.error('Erro ao editar evento:', error);
      setError('Erro ao editar evento. Tente novamente.');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!currentUser?.uid) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const events = userData.events || [];
      
      // Encontrar o evento no array
      const eventToDelete = events.find(e => e.id === eventId);
      
      if (eventToDelete) {
        // Remover o evento do array no Firebase
        await updateDoc(userRef, {
          events: arrayRemove(eventToDelete)
        });
        
        // Atualizar o estado local
        setEvents(prev => prev.filter(e => e.id !== eventId));
      }
    } catch (error) {
      console.error('Erro ao deletar evento:', error);
      setError('Erro ao deletar evento. Tente novamente.');
    }
  };

  return (
    <PageLayout
      title="Eventos"
      customColors={customColors}
      actionButtons={
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          '& .MuiButton-root': {
            flex: { xs: 1, sm: 'initial' },
            minWidth: { xs: 0, sm: 'auto' },
            px: { xs: 1.5, sm: 3 },
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.75rem', sm: '1rem' }
          }
        }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleRemoveAllEvents}
          >
            Remover Todos
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
              '&:hover': {
                bgcolor: customColors?.primary || 'primary.main',
                opacity: 0.9
              }
            }}
          >
            Novo Evento
          </Button>
        </Box>
      }
      compactHeader
    >
      <Grid container spacing={2}>
        {events.map((event) => (
          <Grid item xs={12} sm={6} md={3} key={event.id}>
            <Paper
              elevation={1}
              sx={{ 
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.1)
                }
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {event.title}
                </Typography>
                {event.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {event.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Data programada: {formatDate(event.date)}
                  </Typography>
                  {event.time && (
                    <Typography variant="caption" color="text.secondary">
                      Horário: {formatTime(event.time)}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: 1,
                mt: 2
              }}>
                <IconButton
                  edge="end"
                  onClick={() => {
                    setEditingEvent(event);
                    setOpenEditDialog(true);
                  }}
                  sx={{ 
                    color: customColors?.primary || 'primary.main',
                    '&:hover': {
                      bgcolor: alpha(customColors?.primary || theme.palette.primary.main, 0.1)
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => {
                    setEventToDelete(event.id);
                    setDeleteConfirmDialog(true);
                  }}
                  sx={{ 
                    color: 'error.main',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.error.main, 0.1)
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Dialog para Novo Evento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Evento</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Título"
              fullWidth
              value={newEvent.title}
              onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
              required
              error={!newEvent.title}
              helperText={!newEvent.title ? "Título é obrigatório" : ""}
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={4}
              value={newEvent.description}
              onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data"
                value={newEvent.date}
                onChange={(date) => setNewEvent(prev => ({ ...prev, date }))}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    required
                    error={!newEvent.date}
                    helperText={!newEvent.date ? "Data é obrigatória" : ""}
                  />
                )}
              />
              <TimePicker
                label="Horário (opcional)"
                value={newEvent.time}
                onChange={(time) => setNewEvent(prev => ({ ...prev, time }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleAddEvent}
            variant="contained"
            disabled={!newEvent.title || !newEvent.date}
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              '&:hover': {
                bgcolor: alpha(customColors?.primary || theme.palette.primary.main, 0.9)
              }
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Editar Evento */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Evento</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Título"
              fullWidth
              value={editingEvent?.title}
              onChange={(e) => setEditingEvent(prev => ({ ...prev, title: e.target.value }))}
              required
              error={!editingEvent?.title}
              helperText={!editingEvent?.title ? "Título é obrigatório" : ""}
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={4}
              value={editingEvent?.description}
              onChange={(e) => setEditingEvent(prev => ({ ...prev, description: e.target.value }))}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Data"
                value={editingEvent?.date}
                onChange={(date) => setEditingEvent(prev => ({ ...prev, date }))}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    required
                    error={!editingEvent?.date}
                    helperText={!editingEvent?.date ? "Data é obrigatória" : ""}
                  />
                )}
              />
              <TimePicker
                label="Horário (opcional)"
                value={editingEvent?.time}
                onChange={(time) => setEditingEvent(prev => ({ ...prev, time }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleEditEvent}
            variant="contained"
            disabled={!editingEvent?.title || !editingEvent?.date}
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              '&:hover': {
                bgcolor: alpha(customColors?.primary || theme.palette.primary.main, 0.9)
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog
        open={deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este evento? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialog(false)}
            sx={{ color: 'text.secondary' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => {
              handleDeleteEvent(eventToDelete);
              setDeleteConfirmDialog(false);
            }}
            color="error"
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={successSnackbar} autoHideDuration={3000} onClose={() => setSuccessSnackbar(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccessSnackbar(false)} severity="success" elevation={6} variant="filled">
          Alteração efetuada com sucesso
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}

export default Events;